<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-xl-5"
      :page="pageConfig"
      right-side-class="col-xl-7 align-items-end d-flex pb-3 justify-content-end"
    />
    <filter-manager
      ref="filterComponent"
      v-model="selectedFilters"
      class="mb-4"
      :filters="filtersConfig"
      @search="onSearchHandler()"
    />
    <separator class="my-3" text="Resultado de Dispensas" />
    <search-result
      :educative-regions="educativeRegions"
      :result="result"
      @on-refresh-result="onRefreshResult"
    />
    <pagination
      v-if="showPagination"
      :data="pagination"
      :show-text="true"
      @pagination-go-page="goToPage"
    />
  </div>
</template>

<script>
import storeMixin from 'utils/mixins/storeGenerator';
import PageHeader from 'PageHeader';
import FilterManager from '@/shared/components/FilterManager';
import Separator from '@/shared/components/Separator';
import SearchResult from './SearchResult';
import Pagination from 'pagination';
import roles from '@/utils/constants/roles.js';
import {
  getDispenseAcademicYears,
  getDispenseStatus,
  getSupervisorSpecialist as _getSupervisorSpecialist,
  getDispenseProviders,
  getDispenses as _getDispenses,
} from '@/modules/provider/api';
import { getSpecialistByUser } from '@/modules/provider/api';
import { getMonths } from '@/modules/trainingHistory/api';
import { getRegions as _getRegions } from '@/modules/shared/location.api.js';
import { getSysparameterByNames } from '@/modules/shared/sysparameter.api';
import sysparameterByNames from '@/utils/constants/sysParameterNames';

const STORE_MODULE_NAME = 'DispenseManagementModule';
import exemptionType from '@/utils/shared/constants/exemptionType';

const DE_PROVIDER_ID = 472;

export default {
  name: 'MainExemption',
  components: {
    PageHeader,
    FilterManager,
    Separator,
    SearchResult,
    Pagination,
  },
  mixins: [storeMixin],
  data: () => ({
    moduleName: STORE_MODULE_NAME,
    filters: {
      academicYears: [],
      providers: [],
      statuses: [],
      months: [],
      specialistList: [],
      currentAcademicYear: null,
    },
    selectedFilters: {
      academicYear: null,
      provider: null,
      status: null,
      month: null,
      specialist: null,
      exemptionType: null,
    },
    result: {
      data: [],
    },
    pagination: {
      currentPage: 1,
      totalItemCount: 0,
      pageCount: 0,
      pageSize: 10,
      count: 0,
    },
    exemptionTypeOptions: [
      {
        id: exemptionType.Corporación,
        name: 'Corporación',
        permission: 'provider.exemptions.seeSupervisionExemptions',
      },
      {
        id: exemptionType.EspecialistaDEPR,
        name: 'Especialista DEPR',
        permission: 'provider.exemptions.seeDEexemptions',
      },
      {
        id: exemptionType.EspecialistaRempro,
        name: 'Especialista Rempro',
        permission: 'provider.exemptions.seeRemproExemptions',
      },
    ],
    educativeRegions: [],
    parameterValues: [],
    specialistId: null,
    canCreateDEExemption: false,
  }),
  computed: {
    defaultExemptionType() {
      if (this.availableExemptionTypes.length == 1) {
        this.onExemptionTypeChanged(this.availableExemptionTypes[0]);
        return this.availableExemptionTypes[0];
      }
      return null;
    },
    canCreateExemption() {
      return (
        this.canCreateDEExemption &&
        this.userHasPermissions('provider.dispenseManagement.create')
      );
    },
    headerActionsConfig() {
      return [
        {
          text: 'Crear Dispensa',
          icon: 'fas fa-user-tie',
          variant: 'outline-success ml-0',
          click: this.createDispense,
          visible: this.canCreateExemption,
        },
      ];
    },
    availableExemptionTypes() {
      return this.exemptionTypeOptions.filter(
        (o) => o.permission == null || this.userHasPermissions(o.permission)
      );
    },
    isDeprExemptionEnable() {
      const paramValue = this.parameterValues.find(
        (x) =>
          x.name ==
          sysparameterByNames.CanCreateDEPRSpecialistsOtherCorporations
      );

      return !paramValue
        ? false
        : paramValue.staticValue.toUpperCase() == 'TRUE';
    },
    pageConfig() {
      return {
        icon: 'fas fa-user-tie',
        title: 'Manejo de Dispensas',
        routes: ['Inicio', 'Proveedor', 'Manejo de Dispensas'],
      };
    },
    showPagination() {
      return (
        this.result.data.length > 0 &&
        this.result.totalItemCount > this.defaultPageSize
      );
    },
    defaultPageSize() {
      return 10;
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filters.academicYears,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: false,
          class: 'col-md-3',
          initialValue: this.selectedFilters.academicYear,
          onChanged: this.onYearChanged,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filters.statuses,
          placeholder: 'Seleccione estatus',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-3',
          onChanged: this.onStatusChanged,
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filters.providers,
          placeholder: 'Seleccione Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-3',
          onChanged: this.onProviderChanged,
        },
        {
          name: 'ExemptionType',
          component: 'CustomDropDown',
          options: this.availableExemptionTypes,
          placeholder: 'Seleccione Tipo',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-3',
          disabled: this.defaultExemptionType != null,
          initialValue: this.defaultExemptionType,
          onChanged: this.onExemptionTypeChanged,
          visible: this.isDeprExemptionEnable,
        },
        {
          name: 'month',
          component: 'CustomDropDown',
          label: 'Mes:',
          options: this.filters.months,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-3',
          isAdvanced: true,
          onChanged: this.onMonthChanged,
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          label: 'Especialista Supervisor:',
          options: this.filters.specialistList,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-7',
          isAdvanced: true,
          onChanged: this.onSpecialistChanged,
        },
      ];
    },
    isSpecialist() {
      return [roles.Especialista].includes(this.userRole);
    },
  },
  watch: {
    isDeprExemptionEnable(newValue) {
      if (!newValue) {
        this.selectedFilters.exemptionType = this.exemptionTypeOptions.find(
          (x) => x.id == exemptionType.Corporación
        );
      }
    },
  },
  destroyed() {
    this.persistData();
  },
  async mounted() {
    await this.fetchParameters();
    await this.onInit();
  },
  methods: {
    createDispense() {
      this.$router.push({
        name: 'dispensation-request',
        params: {
          specialistId: this.specialistId,
          companyId: DE_PROVIDER_ID,
          academicYearId: this.filters.academicYears[0].id,
          exemptionType: exemptionType.EspecialistaDEPR,
          specialistSupervisions: [],
        },
      });
    },
    async fetchParameters() {
      const { data } = await getSysparameterByNames({
        parameterNames: [
          sysparameterByNames.CanCreateDEPRSpecialistsOtherCorporations,
        ],
      });

      this.parameterValues = [...data];
    },
    goToPage(page) {
      this.onSearchHandler(page);
    },
    async onSearchHandler(page = 1) {
      const { data } = await _getDispenses(
        this.selectedFilters.academicYear.id,
        this.selectedFilters.month?.id ?? null,
        this.selectedFilters.month?.year ?? null,
        this.selectedFilters.provider?.id ?? null,
        this.selectedFilters.status?.id ?? null,
        this.selectedFilters.specialist?.id ?? null,
        this.selectedFilters.exemptionType?.id
          ? [this.selectedFilters.exemptionType.id]
          : this.availableExemptionTypes.map((x) => x.id),
        page
      );

      this.result = data;

      this.pagination = {
        ...this.pagination,
        currentPage: data.currentPage,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        count: data.count,
      };
    },
    async onInit() {
      const self = this;
      getDispenseStatus().then((res) => (self.filters.statuses = res.data));

      getDispenseAcademicYears().then(async (res) => {
        const { data } = res;
        self.filters.academicYears = data.reverse();

        let currentAcademicYear =
          self.filters.academicYears.length > 0
            ? self.filters.academicYears[0]
            : null;

        self.filters.currentAcademicYear = currentAcademicYear;
        self.selectedFilters.academicYear =
          self.selectedFilters.academicYear ?? currentAcademicYear;

        if (self.selectedFilters.academicYear === null) return;

        await this.getEducativeRegions();

        getMonths(self.selectedFilters.academicYear.id).then((res) => {
          const { data } = res;
          self.filters.months = data.map((item) => {
            return {
              id: item.monthNumber,
              name: item.monthDesc,
              year: item.year,
            };
          });
        });

        getDispenseProviders(self.selectedFilters.academicYear.id)
          .then((res) => {
            const { data } = res;
            self.filters.providers = data;

            if (self.filters.providers.length == 1) {
              this.selectedFilters.provider = self.filters.providers[0];
              this.selectedFilters.provider.disabled = true;
            }
          })
          .catch(() => (self.filters.providers = []));

        if (this.isSpecialist) {
          const userId = this.$store.state['$_user'].user.profile.sub;
          const { data: specialist } = await getSpecialistByUser(userId);
          this.specialistId = specialist.id;
          this.canCreateDEExemption = specialist.canCreateDEExemption;
          this.filters.specialistList = [
            {
              name: specialist.fullName,
              id: specialist.id,
              selected: true,
              disabled: true,
            },
          ];
          this.selectedFilters.specialist = this.filters.specialistList[0];
          this.selectedFilters.specialist.disabled = true;
        } else {
          self.getSupervisorSpecialist();
        }
      });
    },
    async getEducativeRegions() {
      const { data } = await _getRegions({
        academicYearId: this.selectedFilters.academicYear?.id,
      });
      this.educativeRegions = data;
    },
    async onYearChanged(model) {
      const self = this;

      self.selectedFilters.academicYear =
        model || self.filters.currentAcademicYear;

      await this.getEducativeRegions();

      getMonths(self.selectedFilters.academicYear.id).then((res) => {
        const { data } = res;
        self.filters.months = data.map((item) => {
          return {
            id: item.monthNumber,
            name: item.monthDesc,
            year: item.year,
          };
        });
        self.onMonthChanged(null, true);
      });

      if (!this.isSpecialist) {
        getDispenseProviders(self.selectedFilters.academicYear.id)
          .then((res) => {
            const { data } = res;
            self.filters.providers = data;
            self.onProviderChanged(null);
          })
          .catch(() => {
            self.filters.providers = [];
            self.onProviderChanged(null);
          });
      }
    },
    onProviderChanged(model, noLoad) {
      const self = this;
      self.selectedFilters.provider = model;
      if (!this.isSpecialist) self.getSupervisorSpecialist(noLoad);
    },
    onStatusChanged(model) {
      this.selectedFilters.status = model;
    },
    onMonthChanged(model, noLoad) {
      const self = this;
      self.selectedFilters.month = model;
      if (!this.isSpecialist) self.getSupervisorSpecialist(noLoad);
    },
    onSpecialistChanged(model) {
      this.selectedFilters.specialist = model;
    },
    onExemptionTypeChanged(model) {
      this.selectedFilters.exemptionType = model;
    },
    getSupervisorSpecialist(noLoad) {
      const self = this;
      self.onSpecialistChanged(null);

      if (noLoad !== undefined) return;

      var payload = {
        fiscalYearId: self.selectedFilters.academicYear.id,
        companyId: self.selectedFilters.provider?.id,
        month: self.selectedFilters.month?.id,
        year: self.selectedFilters.month?.year,
      };

      _getSupervisorSpecialist(payload).then((res) => {
        const { data } = res;
        self.filters.specialistList = data;
      });
    },
    onRefreshResult() {
      this.onSearchHandler();
    },
  },
};
</script>
